import { MKLog } from "./MKLog";
import { MKUtils } from "./MKUtils";
import { MKHttpMethod } from "../../alias";
import { MKHttpRequest } from "../request";
import { MKHttpResponse } from "../../interfaces";
import { sign as naclSignature } from "tweetnacl";


/**
 * Secure URL manager.
 *
 * This class will manage all tasks related to generating key-pairs and signatures for given URLs.
 *
 * @internal
 */
export class MKSecureUrlManager {
    private kssUrl: string;
    private authToken: string;
    private httpRequest: MKHttpRequest;
    private ed25519Keys: ED25519Keys;
    private lastRequestInfo: RequestInfo;
    private signatureGenerationTime: number = 0;      // time taken in milliseconds to generate the signature.

    constructor(authToken: string, kssUrl: string) {
        this.kssUrl = kssUrl;
        this.authToken = authToken;
        this.httpRequest = new MKHttpRequest();
        this.ed25519Keys = {
            publicKey: "",
            privateKey: new Uint8Array()
        };
        this.lastRequestInfo = {
            url: "",
            method: "GET"
        };
    }

    /**
     * Generate a new signature for the given request URL.
     * @param url the request URL to secure.
     * @param method the method used for the request URL.
     * @param excludeParams the keys/parms to exclude from the signature.
     * @returns a promise that resolves to a generated signature, else rejects with an error message.
     */
    async getSignature(url: string, method: MKHttpMethod = "GET", excludeParams?: string[]): Promise<string> {
        const startTime = Date.now();     // start timestamp
        this.signatureGenerationTime = 0; // reset on start

        return new Promise<string>(async (resolve, reject) => {
            try {
                // generate/update ED25519 keys
                this.updateED25519Keys();

                // get a new signedKey from KSS
                const signedKey = await this.signKeyRequest()

                // get signedJson for the given request URL
                const signedJson = this.getSignedJson(url, method, excludeParams);

                // generate signature based on the signedJson
                const signature = MKUtils.base64EncodeUint8Array(naclSignature.detached(MKUtils.stringToUint8Array(signedJson), this.ed25519Keys.privateKey));

                // update time taken (when request signature generation succeeded)
                this.signatureGenerationTime = Date.now() - startTime;

                // Resolve with the signature
                return resolve(MKUtils.base64Encode(MKUtils.jsonStringify({
                    signedKey: signedKey,
                    signedRequest: {
                        signedJson: signedJson,
                        signature: signature
                    }
                })));
            } catch (error) {
                // update time taken (when request signature generation failed)
                this.signatureGenerationTime = Date.now() - startTime;
                return reject(error);
            }
        });
    }

    /**
     * Update STS authToken and get a new signature.
     *
     * Note: Signature will be generated only if previous request information is available, else promise will resolve to an empty string indicating that only AuthToken was updated but no signature generated.
     *
     * @param authToken the new STS authToken to update.
     * @returns a promise that resolves to a generated signature, else rejects with an error message.
     */
    async updateAuthToken(authToken: string): Promise<string> {
        if (!!this.authToken) {
            // update authToken
            this.authToken = authToken;

            // generate new signature with new authToken and return signature
            if (!!this.lastRequestInfo.url && !!this.lastRequestInfo.method) {
                return this.getSignature(this.lastRequestInfo.url, this.lastRequestInfo.method, this.lastRequestInfo.excludeParams);
            } else {
                // resolve with empty string...
                return Promise.resolve("");
            }
        }
        return Promise.reject()
    }

    getSignatureGenerationTime(): number {
        return this.signatureGenerationTime;
    }

    /**
     * KSS signKey request.
     * @returns a promise which will resolve to a string containing the received signedKey and reject with error message in case of failure.
     */
    private async signKeyRequest(): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            if (!!!this.kssUrl || !!!this.authToken || !!!this.ed25519Keys.publicKey) {
                return reject(new Error("Invalid input for signKey request!"));
            }

            // KSS signKey request
            this.httpRequest.post(this.kssUrl, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `OAUTH2 access_token="${this.authToken}"`
                },
                body: {
                    "publicKey": this.ed25519Keys.publicKey
                }
            })
            .then((response: MKHttpResponse) => {
                if (!response.ok) {
                    return reject(new Error("KSS signKey request failed with HTTP error!"));
                }
                return response.json();
            })
            .then((data: any) => {
                if (MKUtils.isValidObject(data) && MKUtils.isValidString(data.SignedKey)) {
                        return resolve(data.SignedKey);
                } else {
                    return reject(new Error("Malformed KSS signKey response!"));
                }
            })
            .catch((error) => {
                return reject(error);
            });
        });
    }

    /**
     * Update ED25519 keys.
     *
     * The returned keys are base64 encoded strings.
     *
     * @returns a promise which will resolve if keys are generated and updated, reject with error message on failure.
     */
    private updateED25519Keys(): void {
        if (!!this.ed25519Keys.publicKey && this.ed25519Keys.privateKey.length > 0) {
            return; // we retain the same keys...
        }

        // generate new key pairs, update and resolve promise
        const keyPair = naclSignature.keyPair();
        if (keyPair.publicKey.length > 0 && keyPair.secretKey.length > 0) {
            this.ed25519Keys = {
                privateKey: keyPair.secretKey,
                publicKey: MKUtils.base64EncodeUint8Array(keyPair.publicKey)
            };
        } else {
            MKLog.error("[MKSecureUrlManager::updateED25519Keys] Invalid key paris, update failed!");
        }
    }

    /**
     * Get signedJson string to be used for generating signature.
     * @param url the request URL with query params to be secured.
     * @param method the HTTP method used with the request. Default is "GET".
     * @param excludeParams list of keys/params to exclude from the signature.
     * @returns a base64 string with the URL query params and method to be used for generating signature when successful, an empty string otherwise.
     */
    private getSignedJson(url: string, method: MKHttpMethod = "GET", excludeParams?: string[]): string {
        let queryParamsToExclude: string[] = ["hdnts"]; // default
        if (excludeParams && excludeParams.length > 0) queryParamsToExclude = queryParamsToExclude.concat(excludeParams);
        const urlParams = MKUtils.getURLQueryParams(url, queryParamsToExclude);
        if (Object.keys(urlParams).length > 0) {
            const signedJson = {
                m: method,
                qs: urlParams
            }

            // update information about this request
            this.lastRequestInfo = {
                url: url,
                method: method,
                excludeParams: queryParamsToExclude
            };

            // return signedJson
            return MKUtils.base64Encode(MKUtils.jsonStringify(signedJson));
        }
        return "";
    }
}

/**
 * ED25519 Keys.
 * @internal
 */
interface ED25519Keys {
    /**
     * Public key as base64 encoded string.
     *
     * We send the public key to KSS so we need it in base64 string format.
     */
    publicKey: string;

    /**
     * Private Key as Uint8Array.
     *
     * We use the private key to generate signature and hence need it as Uint8Array.
     */
    privateKey: Uint8Array;
}

/**
 * Information about the request used for generating signature.
 * @internal
 */
interface RequestInfo {
    /**
     * Request URL
     */
    url: string;

    /**
     * Request HTTP Method.
     *
     * Default is "GET"
     */
    method: MKHttpMethod;

    /**
     * Keys/Params to exclude from URL query params used to generate the signature.
     */
    excludeParams?: string[];
}